import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { useSelector } from 'react-redux';
import {
	buildOrderTree,
	getMenuItemName,
	getMenuItemQty,
	getMenuItemPrice,
	getSubTotal,
	getMenuItemDetail,
	getMenuItemType,
	normalizeType,
	isDealType,
	isComboType,
	isModifierLine,
	isVariantType,
	isDiscountType,
	getModifierChildNodes,
	getDiscountChildNodes,
	getDiscountAmount,
	getDiscountPercentage,
} from '../../utils/orderDetailsTree';
import {
	hasCancelAudit,
	labelForCancelReasonCode,
	pickOrderField,
} from '../../utils/marketplaceCancelReasonLabels';
import { isPosAllowed } from '../../utils/companyCapabilities';

function ExtraOrderDetails() {
	const { orderId } = useParams();
	const [searchParams] = useSearchParams();
	const [orderData, setOrderData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [compCurrency, setCompCurrency] = useState('GBP');

	// Get invoice number from URL parameters (either from params or query string)
	const invoiceNo = orderId || searchParams.get('invoiceNo');

	// Get company ID from Redux store
	const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
	const profileData = useSelector((state) => state.auth.profileData);

	// Currency symbols mapping
	const currencySymbols = {
		GBP: '£',
		USD: '$',
		EUR: '€',
	};

	const getCurrencySymbol = (currencyCode) => {
		return currencySymbols[currencyCode] || currencyCode;
	};

	// Fetch company currency
	useEffect(() => {
		const fetchCompCurrency = async () => {
			try {
				const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
				const profileData = response.data.data;
				setCompCurrency(profileData?.compCurrency || 'GBP');
			} catch (err) {
				console.error('Failed to load company currency:', err);
			}
		};
		fetchCompCurrency();
	}, []);

	useEffect(() => {
		if (invoiceNo) {
			fetchOrderDetails();
		} else {
			setError('Invoice number is required');
			setLoading(false);
		}
	}, [invoiceNo]);

	const fetchOrderDetails = async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await axiosInstance.get(APIs.GET_ORDER_BY_INVOICE_NO, {
				params: {
					InvoiceNo: invoiceNo,
					CompID: CompID || 0
				}
			});

			if (response.data.status) {
				setOrderData(response.data.data);
			} else {
				setError(response.data.message || 'Failed to fetch order details');
			}
		} catch (err) {
			setError(err.response?.data?.message || 'An error occurred while fetching order details');
		} finally {
			setLoading(false);
		}
	};

	const formatCurrency = (amount) => {
		return `${getCurrencySymbol(compCurrency)}${parseFloat(amount).toFixed(2)}`;
	};

	const formatDateTime = (dateTime) => {
		return new Date(dateTime).toLocaleString();
	};

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="alert alert-danger" role="alert">
				<h4 className="alert-heading">Error!</h4>
				<p>{error}</p>
			</div>
		);
	}

	if (!orderData) {
		return (
			<div className="alert alert-warning" role="alert">
				<h4 className="alert-heading">No Data Found!</h4>
				<p>No order data found for the provided invoice number.</p>
			</div>
		);
	}

	const orderDetailsList = orderData?.orderDetails || orderData?.OrderDetails || [];
	const orderTree = orderDetailsList.length > 0 ? buildOrderTree(orderDetailsList) : [];

	// Customer block: POS orders only. Hide for Go Fatafat marketplace orders (and for GF-only accounts).
	const isMarketplaceOrder = Boolean(
		orderData?.isMarketPlaceOrder ??
			orderData?.IsMarketPlaceOrder ??
			orderData?.isMarketplaceOrder ??
			orderData?.IsMarketplaceOrder
	);
	const showCustomerInfo = isPosAllowed(profileData) && !isMarketplaceOrder;

	const orderStatus = (
		pickOrderField(orderData, 'recvorder_status', 'Recvorder_status') || 'Unknown'
	).toString();
	const statusLower = orderStatus.trim().toLowerCase();
	const statusBadgeClass =
		statusLower === 'completed'
			? 'bg-success'
			: statusLower === 'cancelled' || statusLower === 'rejected'
				? 'bg-danger'
				: 'bg-warning';

	const cancelReasonCode = pickOrderField(orderData, 'cancelReasonCode', 'CancelReasonCode');
	const cancelledByType = pickOrderField(orderData, 'cancelledByType', 'CancelledByType');
	const cancelledByDisplay = pickOrderField(
		orderData,
		'cancelledByDisplay',
		'CancelledByDisplay'
	);
	const cancelledAt = pickOrderField(orderData, 'cancelledAt', 'CancelledAt');
	const freeTextReason = pickOrderField(
		orderData,
		'orderRejectorRefundReason',
		'OrderRejectorRefundReason'
	);
	const showCancelAudit = hasCancelAudit(orderData) || Boolean(freeTextReason);

	const riderAtPickup = pickOrderField(
		orderData,
		'marketplaceRider_AtPickupDateTime',
		'MarketplaceRider_AtPickupDateTime'
	);
	const riderOnTheWay = pickOrderField(
		orderData,
		'marketplaceRider_OnTheWayDateTime',
		'MarketplaceRider_OnTheWayDateTime'
	);

	const renderModifierRows = (modNodes, indentPx = 80) => {
		if (!modNodes?.length) return null;
		return (
			<div className="row mt-2" style={{ marginLeft: `${indentPx}px` }}>
				<div className="col-12">
					<div className="ps-3" style={{ borderLeft: '3px solid #e0e0e0' }}>
						<div style={{ fontSize: '11px', color: '#666', fontWeight: 600, marginBottom: '6px' }}>
							Add-ons:
						</div>
						{modNodes.map((modNode, modIdx) => {
							const mod = modNode.item || {};
							const modQty = getMenuItemQty(mod);
							const modPrice = getMenuItemPrice(mod);
							const modName = getMenuItemName(mod);
							const modVoid = mod.isItemVoid ?? mod.IsItemVoid ?? false;
							const nested = getModifierChildNodes(modNode).filter((c) => !isVariantType(c.item));
							return (
								<div key={`mod-${modNode.id || modIdx}`}>
									<div
										className={`row align-items-center mb-2 ${modVoid ? 'text-decoration-line-through text-muted' : ''}`}
										style={{ fontSize: '12px', ...(modVoid ? { opacity: 0.8 } : {}) }}
									>
										<div className="col-lg-7">
											<span style={{ fontWeight: 600 }}>{modQty}x</span> {modName}
										</div>
										<div className="col-lg-3 text-muted">
											{formatCurrency(modPrice)} x {modQty}
										</div>
										<div className="col-lg-2 text-end fw-bold">
											{formatCurrency(modQty * modPrice)}
										</div>
									</div>
									{nested.length > 0 && renderModifierRows(nested, 12)}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		);
	};

	const renderDiscountRows = (discNodes, indentPx = 80) => {
		if (!discNodes?.length) return null;
		return (
			<div className="row mt-2" style={{ marginLeft: `${indentPx}px` }}>
				<div className="col-12">
					<div className="ps-3" style={{ borderLeft: '3px solid #28a745' }}>
						<div style={{ fontSize: '11px', color: '#28a745', fontWeight: 600, marginBottom: '6px' }}>
							Discount:
						</div>
						{discNodes.map((discNode, discIdx) => {
							const disc = discNode.item || {};
							const discName = getMenuItemName(disc);
							const discAmount = getDiscountAmount(disc);
							const discPct = getDiscountPercentage(disc);
							const discVoid = disc.isItemVoid ?? disc.IsItemVoid ?? false;
							return (
								<div
									key={`disc-${discNode.id || discIdx}`}
									className={`row align-items-center mb-2 ${discVoid ? 'text-decoration-line-through text-muted' : ''}`}
									style={{
										fontSize: '12px',
										color: discVoid ? undefined : '#28a745',
										...(discVoid ? { opacity: 0.8 } : {}),
									}}
								>
									<div className="col-lg-10">
										{discName}
										{discPct != null && (
											<span className="text-muted"> ({discPct}% off)</span>
										)}
									</div>
									<div className="col-lg-2 text-end fw-bold">
										-{formatCurrency(discAmount)}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		);
	};

	const renderOrderNode = (node, depth = 0, isChildItem = false, parentDealItem = null) => {
		const item = node.item || {};
		const type = normalizeType(getMenuItemType(item));
		const isThisDeal = isDealType(item);
		const isThisCombo = isComboType(item);
		const isThisDealOrCombo = isThisDeal || isThisCombo;
		const currentParentDealItem = isThisDealOrCombo ? item : parentDealItem;

		const variants = (node.children || []).filter((c) => isVariantType(c.item));
		const modifiers = getModifierChildNodes(node).filter((c) => !isVariantType(c.item));
		const discounts = getDiscountChildNodes(node);
		const otherChildren = (node.children || []).filter(
			(c) => !isModifierLine(c.item) && !isDiscountType(c.item)
		);

		const itemQty = getMenuItemQty(item);
		const itemPrice = getMenuItemPrice(item);
		const storedSubTotal = getSubTotal(item);
		const itemSubTotal =
			storedSubTotal > 0
				? storedSubTotal
				: isThisDealOrCombo
					? itemPrice * itemQty
					: isChildItem
						? 0
						: itemPrice * itemQty;

		let itemName = getMenuItemName(item);
		if (variants.length > 0) {
			const variantLabel = [
				...new Set(variants.map((v) => getMenuItemName(v.item)).filter(Boolean)),
			].join(', ');
			if (variantLabel) {
				const base = itemName.replace(/\s*\([^)]*\)\s*$/, '').trim();
				itemName = `${base} (${variantLabel})`;
			}
		}

		const itemDetail = getMenuItemDetail(item);
		const itemInstructions = item.recvorder_SInstructions ?? item.Recvorder_SInstructions;
		const detailId = item.recvorderDetail_ID ?? item.RecvorderDetail_ID ?? node.id ?? `${itemName}-${depth}`;
		const isVoid = item.isItemVoid ?? item.IsItemVoid ?? false;
		const parentDealQty = parentDealItem ? getMenuItemQty(parentDealItem) || 1 : 1;
		const effectiveQty = isChildItem ? itemQty * parentDealQty : itemQty;

		const badgeLabel = isThisDeal ? 'Combo Deal' : isThisCombo ? 'Combo' : null;
		const childLabel = isThisDeal ? 'Included items' : isThisCombo ? 'Combo items' : 'Items';
		const iconClass = isThisDealOrCombo
			? 'fa-gift'
			: isChildItem
				? 'fa-check-circle'
				: 'fa-utensils';

		return (
			<div key={detailId} className={depth === 0 ? '' : 'mt-2'}>
				<div
					className={`row align-items-center ${isVoid ? 'text-decoration-line-through text-muted' : ''}`}
					style={{
						...(isVoid ? { opacity: 0.8 } : {}),
						...(isChildItem ? { marginLeft: '12px' } : {}),
					}}
				>
					<div className="col-lg-8 d-flex align-items-center">
						<div
							className={`h-65px w-65px d-flex align-items-center justify-content-center position-relative rounded ${
								isThisDealOrCombo || isChildItem ? 'bg-primary bg-opacity-10' : 'bg-light'
							}`}
							style={isChildItem ? { height: '48px', width: '48px' } : undefined}
						>
							<i
								className={`fa ${iconClass} ${isChildItem ? 'fa-lg' : 'fa-2x'} ${
									isThisDealOrCombo || isChildItem ? 'text-primary' : 'text-muted'
								}`}
							></i>
							{!isModifierLine(item) && !isDiscountType(item) && (
								<span className="w-20px h-20px p-0 d-flex align-items-center justify-content-center badge bg-primary text-white position-absolute end-0 top-0 fw-bold fs-12px rounded-pill mt-n2 me-n2">
									{effectiveQty}
								</span>
							)}
						</div>
						<div className="ps-3 flex-1">
							<div>
								<span className={`${isVoid ? 'text-muted' : 'text-dark'}`}>
									{itemName}
									{badgeLabel && (
										<span className="badge bg-info text-dark ms-2" style={{ fontSize: '10px' }}>
											{badgeLabel}
										</span>
									)}
									{isVoid && (
										<span className="badge bg-secondary ms-1" style={{ textDecoration: 'none' }}>
											VOID
										</span>
									)}
								</span>
							</div>
							{itemDetail && (
								<div className="text-dark text-opacity-50 small">{itemDetail}</div>
							)}
							{itemInstructions && (
								<div className="text-warning small">
									<i className="fa fa-sticky-note me-1"></i>
									{itemInstructions}
								</div>
							)}
							{type === 'DEAL_ITEM' && (item.dealSlotID || item.DealSlotID) && (
								<div className="text-muted" style={{ fontSize: '11px' }}>
									Slot selection
								</div>
							)}
						</div>
					</div>
					<div className="col-lg-2 m-0 ps-lg-3">
						{isChildItem
							? (itemSubTotal > 0 ? formatCurrency(itemPrice) : 'Included')
							: `${formatCurrency(itemPrice)} x ${itemQty}`}
					</div>
					<div className="col-lg-2 text-dark fw-bold m-0 text-end">
						{isChildItem && itemSubTotal <= 0
							? '—'
							: formatCurrency(itemSubTotal)}
					</div>
				</div>

				{otherChildren.length > 0 && (
					<div
						className="mt-3"
						style={{
							marginLeft: isThisDealOrCombo ? '20px' : '80px',
							paddingLeft: '16px',
							borderLeft: isThisDealOrCombo ? '3px solid #0d6efd' : '3px solid #e0e0e0',
						}}
					>
						{isThisDealOrCombo && (
							<div
								style={{
									fontSize: '11px',
									color: '#0d6efd',
									fontWeight: 600,
									marginBottom: '8px',
									textTransform: 'uppercase',
									letterSpacing: '0.03em',
								}}
							>
								{childLabel}
							</div>
						)}
						{otherChildren.map((child, idx) => (
							<div key={child.id || idx} className={idx > 0 ? 'mt-2' : ''}>
								{renderOrderNode(child, depth + 1, isThisDealOrCombo, currentParentDealItem)}
							</div>
						))}
					</div>
				)}

				{modifiers.length > 0 && renderModifierRows(modifiers, isChildItem ? 60 : 80)}
				{discounts.length > 0 && renderDiscountRows(discounts, isChildItem ? 60 : 80)}
			</div>
		);
	};

	return (
		<div>
			<div className="d-flex align-items-center">
				<div>
					<ol className="breadcrumb">
						<li className="breadcrumb-item"><a href="#/">Home</a></li>
						<li className="breadcrumb-item active">Order Details</li>
					</ol>
					<h1 className="page-header">
						Order Details - {orderData?.recvorder_InvoiceNo || 'N/A'}
					</h1>
					<div className="text-muted">
						<small>Status: <span className={`badge ${statusBadgeClass}`}>
							{orderStatus}
						</span></small>
						<small className="ms-3">Date: {orderData?.recvorder_datetime ? formatDateTime(orderData.recvorder_datetime) : 'N/A'}</small>
						{(orderData?.recvorder_FrontOrderID || orderData?.recvorder_FrontOrderId) && (
							<small className="ms-3">Front Order ID: <strong>{orderData.recvorder_FrontOrderID || orderData.recvorder_FrontOrderId}</strong></small>
						)}
					</div>
				</div>
			</div>
			{/* <div className="mb-3 d-md-flex fw-bold">
				<div className="mt-md-0 mt-2"><a href="#/" className="text-decoration-none text-dark"><i className="fa fa-print fa-fw me-1 text-dark text-opacity-50"></i> Print</a></div>
				<div className="ms-md-4 mt-md-0 mt-2"><a href="#/" className="text-decoration-none text-dark"><i className="fa fa-boxes-stacked fa-fw me-1 text-dark text-opacity-50"></i> Restock items</a></div>
				<div className="ms-md-4 mt-md-0 mt-2"><a href="#/" className="text-decoration-none text-dark"><i className="fa fa-pen fa-fw me-1 text-dark text-opacity-50"></i> Edit</a></div>
				<div className="ms-md-4 mt-md-0 mt-2 dropdown-toggle">
					<a href="#/" data-bs-toggle="dropdown" className="text-decoration-none text-dark text-opacity-75"><i className="fa fa-cog fa-fw me-1 text-dark text-opacity-50"></i> More Actions <b className="caret"></b></a>
					<div className="dropdown-menu">
						<a className="dropdown-item" href="#/">Action</a>
						<a className="dropdown-item" href="#/">Another action</a>
						<a className="dropdown-item" href="#/">Something else here</a>
						<div role="separator" className="dropdown-divider"></div>
						<a className="dropdown-item" href="#/">Separated link</a>
					</div>
				</div>
			</div> */}
			<div className="row gx-4">
				<div className="col-xl-8 mb-xl-0 mb-4">
					<div className="card border-0 mb-4">
						<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
							<i className="fa fa-shopping-bag fa-lg me-2 text-gray text-opacity-50"></i>
							Products ({orderTree.length})
							{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500"><i className="fa fa-truck fa-lg me-1"></i> Add Tracking Link</a> */}
						</div>
						<div className="card-body p-3 text-dark fw-bold">
							{orderTree.length > 0 ? (
								orderTree.map((node, idx) => (
									<div key={node.id || idx}>
										{renderOrderNode(node)}
										{idx < orderTree.length - 1 && <hr className="my-4" />}
									</div>
								))
							) : (
								<div className="text-center text-muted py-4">
									<i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
									<p>No items found in this order</p>
								</div>
							)}
						</div>
						{/* <div className="card-footer bg-none d-flex p-3">
							<a href="#/" className="btn btn-default ms-auto">More <b className="caret"></b></a>
							<a href="#/" className="btn btn-primary ms-2">Add Tracking</a>
						</div> */}
					</div>
					<div className="card border-0">
					<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
						<i className="fa fa-credit-card fa-lg me-2 text-gray text-opacity-50"></i>
						Payment Records
						{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500"><i className="fab fa-paypal me-1 fa-lg"></i> View paypal records</a> */}
					</div>
						<div className="card-body">
							<table className="table table-borderless table-sm fw-bold m-0">
								<tbody>
									<tr>
										<td className="w-150px">Subtotal</td>
										<td>{orderTree.length} items</td>
										<td className="text-end">{formatCurrency((orderData?.recvorder_Totalamount || 0) - (orderData?.vatAmount || 0) - (orderData?.recvorder_Servicecharges || 0) + (orderData?.recvorder_Totaldiscount || 0))}</td>
									</tr>
									{orderData?.vatAmount > 0 && (
										<tr>
											<td>Tax ({orderData?.recvorder_VATType})</td>
											<td>VAT {orderData?.recvorder_VATpercentage || 0}%</td>
											<td className="text-end">{formatCurrency(orderData?.vatAmount || 0)}</td>
										</tr>
									)}
									{orderData?.recvorder_Servicecharges > 0 && (
										<tr>
											<td>Service Charges</td>
											<td>{orderData?.recvorder_Servicechargespercentage?.toFixed(2) || 0}%</td>
											<td className="text-end">{formatCurrency(orderData?.recvorder_Servicecharges || 0)}</td>
										</tr>
									)}
									{orderData?.recvorder_Totaldiscount > 0 && (
										<tr>
											<td>Discount</td>
											<td>{orderData?.recvorder_Totaldiscountpercentage || 0}% off</td>
											<td className="text-end text-success">-{formatCurrency(orderData?.recvorder_Totaldiscount || 0)}</td>
										</tr>
									)}
									{(() => {
										const promoCode = orderData?.recvorder_promotioncode || "";
										const promoDiscount = parseFloat(orderData?.recvorder_promotionDiscountAmount || 0);
										if (promoCode && promoCode.trim() !== "" && promoDiscount > 0) {
											return (
												<tr>
													<td>Promo Code</td>
													<td><u className="text-success">{promoCode}</u></td>
													<td className="text-end text-success">-{formatCurrency(promoDiscount)}</td>
												</tr>
											);
										}
										return null;
									})()}
									{orderData?.recvorder_TipAmount > 0 && (
										<tr>
											<td>Tip</td>
											<td></td>
											<td className="text-end">{formatCurrency(orderData?.recvorder_TipAmount || 0)}</td>
										</tr>
									)}
									{(() => {
										const extraCharges = parseFloat(orderData?.recvorder_ExtraCharges || orderData?.Recvorder_ExtraCharges || 0);
										return extraCharges > 0 ? (
											<tr>
												<td>Extra Charges</td>
												<td></td>
												<td className="text-end">{formatCurrency(extraCharges)}</td>
											</tr>
										) : null;
									})()}
									{orderData?.recvorder_Deliverycharges > 0 && (
										<tr>
											<td>Delivery charges</td>
											<td></td>
											<td className="text-end">{formatCurrency(orderData?.recvorder_Deliverycharges || 0)}</td>
										</tr>
									)}
									<tr>
										<td className="pb-2" colSpan="2"><b>Total</b></td>
										<td className="text-end pb-2 text-decoration-underline"><b>{formatCurrency(orderData?.recvorder_Totalamount || 0)}</b></td>
									</tr>
									<tr>
										<td colSpan="3">
											<hr className="m-0" />
										</td>
									</tr>
									{(() => {
										const paymentMethod = (orderData?.recvorder_Paymentmethod || "").toLowerCase();
										const isMixPayment = paymentMethod.includes("mix") || (orderData?.paymentDetails && orderData.paymentDetails.length > 1);
										
										if (isMixPayment && orderData?.paymentDetails && orderData.paymentDetails.length > 0) {
											return (
												<>
													<tr>
														<td className="pt-2 pb-2" colSpan="3" style={{ fontWeight: "bold", fontSize: "13px" }}>
															MIX Payment Details:
														</td>
													</tr>
													{orderData.paymentDetails.map((payment, index) => (
														<tr key={payment.id || index}>
															<td className="pt-2 pb-2" nowrap="true">
																Paid by customer
															</td>
															<td className="pt-2 pb-2">
																via <span className="text-primary">{payment.paymentmethod || "N/A"}</span>
																{payment.cr_ReferenceNo && ` (#${payment.cr_ReferenceNo})`}
																{payment.cr_CardBrand && ` - ${payment.cr_CardBrand}`}
															</td>
															<td className="pt-2 pb-2 text-end">{formatCurrency(payment.amount || 0)}</td>
														</tr>
													))}
												</>
											);
										} else if (orderData?.paymentDetails && orderData.paymentDetails.length > 0) {
											return orderData.paymentDetails.map((payment, index) => (
												<tr key={payment.id || index}>
													<td className="pt-2 pb-2" nowrap="true">
														Paid by customer
													</td>
													<td className="pt-2 pb-2">
														via <span className="text-primary">{payment.paymentmethod || "N/A"}</span>
														{payment.cr_ReferenceNo && ` (#${payment.cr_ReferenceNo})`}
														{payment.cr_CardBrand && ` - ${payment.cr_CardBrand}`}
													</td>
													<td className="pt-2 pb-2 text-end">{formatCurrency(payment.amount || 0)}</td>
												</tr>
											));
										}
										return null;
									})()}
								</tbody>
							</table>
						</div>
						<div className="card-footer bg-none d-flex p-3">
							{orderData?.recvorder_Paymentrecvstatus === 'Completed' && (
								<span className="badge bg-success ms-auto">Payment Completed</span>
							)}
						</div>
					</div>
				</div>
				<div className="col-xl-4">
					<div className="card border-0 mb-4">
					<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
						Notes
						{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500">Edit</a> */}
					</div>
						<div className="card-body">
							{orderData?.recvorder_comments ? (
								<div>
									<strong>Order Comments:</strong> {orderData.recvorder_comments}
								</div>
							) : (
								<div className="text-muted">No notes from customer</div>
							)}
							{orderData?.recorder_Ackcomments && (
								<div className="mt-2">
									<strong>Acknowledgment Comments:</strong> {orderData.recorder_Ackcomments}
								</div>
							)}
						</div>
					</div>
					{showCancelAudit && (
						<div className="card border-0 mb-4 border-danger border-opacity-25">
							<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center text-danger">
								<i className="fa fa-ban me-2"></i>
								Cancel / Reject Details
							</div>
							<div className="card-body fw-bold">
								{cancelReasonCode && (
									<div className="mb-2">
										<strong>Reason code:</strong>{' '}
										{labelForCancelReasonCode(cancelReasonCode)}
										{labelForCancelReasonCode(cancelReasonCode) !== cancelReasonCode && (
											<span className="text-muted small ms-1">({cancelReasonCode})</span>
										)}
									</div>
								)}
								{(cancelledByType || cancelledByDisplay) && (
									<div className="mb-2">
										<strong>Cancelled by:</strong>{' '}
										{[cancelledByType, cancelledByDisplay].filter(Boolean).join(' — ')}
									</div>
								)}
								{cancelledAt && (
									<div className="mb-2">
										<strong>Cancelled at:</strong>
										<br />
										<small className="text-muted">{formatDateTime(cancelledAt)}</small>
									</div>
								)}
								{freeTextReason && (
									<div className="mb-0">
										<strong>Note:</strong> {freeTextReason}
									</div>
								)}
								{!cancelReasonCode && !cancelledByType && !freeTextReason && (
									<div className="text-muted">No cancel audit details recorded.</div>
								)}
							</div>
						</div>
					)}
					{showCustomerInfo && (
					<div className="card border-0 mb-4">
					<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
						Customer
						{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500">Edit</a> */}
					</div>
						<div className="card-body fw-bold">
							<div className="d-flex align-items-center">
								<div className="d-block">
									<div className="h-45px w-45px d-flex align-items-center justify-content-center bg-primary text-white rounded-pill">
										<i className="fa fa-user"></i>
									</div>
								</div>
								<div className="flex-1 ps-3">
									<div className="d-block text-decoration-none">
										{orderData?.customerName || 'Guest Customer'}
									</div>
									{orderData?.customerGroupID > 0 && (
										<div className="text-muted small">
											Group ID: {orderData.customerGroupID}
										</div>
									)}
								</div>
							</div>
							{(() => {
								const isDeliveryOrder = (orderData?.recvorder_Ordertype || "").toLowerCase() === "delivery";
								const deliveryAddress = orderData?.recvOrder_DeliveryAddress || "";
								if (isDeliveryOrder && deliveryAddress && deliveryAddress.trim() !== "" && deliveryAddress !== "N/A") {
									return (
										<div className="mt-3">
											<strong>Delivery Address:</strong><br />
											<span className="fw-normal">{deliveryAddress}</span>
										</div>
									);
								}
								return null;
							})()}
						</div>
					</div>
					)}
					<div className="card border-0 mb-4">
					<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
						Order Information
						{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500">Edit</a> */}
					</div>
						<div className="card-body fw-bold">
							<div className="mb-2">
								<strong>Front Order ID:</strong> {orderData?.recvorder_FrontOrderID ?? orderData?.recvorder_FrontOrderId ?? 'N/A'}
							</div>
							<div className="mb-2">
								<strong>Order Type:</strong>
								<span className={`badge ms-2 ${orderData?.recvorder_Ordertype === 'takeAway' ? 'bg-info' : orderData?.recvorder_Ordertype === 'dineIn' ? 'bg-success' : 'bg-warning'}`}>
									{orderData?.recvorder_Ordertype || 'N/A'}
								</span>
							</div>
							{(orderData?.recvorder_Tableid || orderData?.t_Name || orderData?.t_name) && (
								<div className="mb-2">
									<strong>Table:</strong> {orderData?.t_Name || orderData?.t_name || orderData.recvorder_Tableid}
								</div>
							)}
							<div className="mb-2">
								<strong>Payment Method:</strong> {(() => {
									const paymentMethod = (orderData?.recvorder_Paymentmethod || "").toLowerCase();
									if (paymentMethod.includes("mix") || (orderData?.paymentDetails && orderData.paymentDetails.length > 1)) {
										return "MIX";
									}
									return orderData?.recvorder_Paymentmethod || 'N/A';
								})()}
								{(() => {
									const paymentMethod = (orderData?.recvorder_Paymentmethod || "").toLowerCase();
									const isMixPayment = paymentMethod.includes("mix") || (orderData?.paymentDetails && orderData.paymentDetails.length > 1);
									const paymentDetails = orderData?.paymentDetails || [];
									
									if (isMixPayment && paymentDetails.length > 0) {
										return (
											<div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
												{paymentDetails.map((payment, idx) => (
													<div key={idx} style={{ marginBottom: "2px" }}>
														• {payment.paymentmethod || "N/A"}: {formatCurrency(payment.amount || 0)}
													</div>
												))}
											</div>
										);
									}
									return null;
								})()}
							</div>
							<div className="mb-2">
								<strong>Payment Status:</strong>
								<span className={`badge ms-2 ${orderData?.recvorder_Paymentrecvstatus === 'Completed' ? 'bg-success' : 'bg-warning'}`}>
									{orderData?.recvorder_Paymentrecvstatus || 'Unknown'}
								</span>
							</div>
							<div className="mb-2">
								<strong>Till:</strong> {orderData?.tillname || 'N/A'}
							</div>
							{(orderData?.assignedRiderDisplayName || orderData?.AssignedRiderDisplayName || orderData?.assignedRider || orderData?.AssignedRider) && (
								<div className="mb-2">
									<strong>Rider:</strong> {orderData?.assignedRiderDisplayName || orderData?.AssignedRiderDisplayName || orderData?.assignedRider || orderData?.AssignedRider}
								</div>
							)}
							{orderData?.recvorder_ReadyMins && (
								<div className="mb-2">
									<strong>Ready Time:</strong> {orderData.recvorder_ReadyMins} minutes
								</div>
							)}
						</div>
					</div>
					<div className="card border-0 mb-4">
					<div className="card-header bg-none p-3 h6 m-0 d-flex align-items-center">
						Order Timeline
						{/* <a href="#/" className="ms-auto text-decoration-none text-gray-500">View Details</a> */}
					</div>
						<div className="card-body fw-bold">
							<div className="mb-2">
								<strong>Order Placed:</strong><br />
								<small className="text-muted">{orderData?.recvorder_datetime ? formatDateTime(orderData.recvorder_datetime) : 'N/A'}</small>
							</div>
							{orderData?.recvorder_Ackdatetime && (
								<div className="mb-2">
									<strong>Order Acknowledged:</strong><br />
									<small className="text-muted">{formatDateTime(orderData.recvorder_Ackdatetime)}</small>
								</div>
							)}
							{orderData?.recvorder_AcceptAtKitchenDateTime && (
								<div className="mb-2">
									<strong>Accepted at Kitchen:</strong><br />
									<small className="text-muted">{formatDateTime(orderData.recvorder_AcceptAtKitchenDateTime)}</small>
								</div>
							)}
							{orderData?.recvorder_ReadyAtKitchenDateTime && (
								<div className="mb-2">
									<strong>Ready at Kitchen:</strong><br />
									<small className="text-muted">{formatDateTime(orderData.recvorder_ReadyAtKitchenDateTime)}</small>
								</div>
							)}
							{orderData?.recvorder_OrderCompletedDateTime && (
								<div className="mb-2">
									<strong>Order Completed:</strong><br />
									<small className="text-muted">{formatDateTime(orderData.recvorder_OrderCompletedDateTime)}</small>
								</div>
							)}
							{riderAtPickup && (
								<div className="mb-2">
									<strong>Rider at Pickup:</strong><br />
									<small className="text-muted">{formatDateTime(riderAtPickup)}</small>
								</div>
							)}
							{riderOnTheWay && (
								<div className="mb-2">
									<strong>Rider On the Way:</strong><br />
									<small className="text-muted">{formatDateTime(riderOnTheWay)}</small>
								</div>
							)}
							{cancelledAt && (
								<div className="mb-2">
									<strong>Cancelled / Rejected:</strong><br />
									<small className="text-muted">{formatDateTime(cancelledAt)}</small>
								</div>
							)}
							{orderData?.recvorder_UpdateDateTime && (
								<div className="mb-2">
									<strong>Last Updated:</strong><br />
									<small className="text-muted">{formatDateTime(orderData.recvorder_UpdateDateTime)}</small>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ExtraOrderDetails;